import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import WarningCircle from '@phosphor-icons/core/bold/warning-circle-bold.svg';
import { addStyle, View } from '@khanacademy/wonder-blocks-core';
import { semanticColor, sizing } from '@khanacademy/wonder-blocks-tokens';
import { LabelMedium, LabelSmall } from '@khanacademy/wonder-blocks-typography';
import { PhosphorIcon } from '@khanacademy/wonder-blocks-icon';

const defaultLabeledFieldLabels = {
  errorIconAriaLabel: "Error:"
};
const StyledSpan = addStyle("span");
function LabeledField(props) {
  const {
    field,
    styles: stylesProp,
    label,
    id,
    required,
    testId,
    description,
    errorMessage,
    labels = defaultLabeledFieldLabels
  } = props;
  const generatedUniqueId = React.useId();
  const uniqueId = id != null ? id : `${generatedUniqueId}-labeled-field`;
  const labelId = `${uniqueId}-label`;
  const descriptionId = `${uniqueId}-description`;
  const fieldId = `${uniqueId}-field`;
  const errorId = `${uniqueId}-error`;
  const isRequired = !!required || !!field.props.required;
  const hasError = !!errorMessage || !!field.props.error;
  function renderLabel() {
    const requiredIcon = React.createElement(StyledSpan, {
      style: [styles.textWordBreak, styles.required],
      "aria-hidden": true
    }, " ", "*");
    return React.createElement(React.Fragment, null, React.createElement(LabelMedium, {
      style: [styles.textWordBreak, styles.label, description ? styles.labelWithDescription : styles.labelWithNoDescription, stylesProp == null ? void 0 : stylesProp.label],
      tag: "label",
      htmlFor: fieldId,
      testId: testId && `${testId}-label`,
      id: labelId
    }, label, isRequired && requiredIcon));
  }
  function maybeRenderDescription() {
    if (!description) {
      return null;
    }
    return React.createElement(React.Fragment, null, React.createElement(LabelSmall, {
      style: [styles.textWordBreak, styles.description, stylesProp == null ? void 0 : stylesProp.description],
      testId: testId && `${testId}-description`,
      id: descriptionId
    }, description));
  }
  function maybeRenderError() {
    return React.createElement(React.Fragment, null, React.createElement(View, {
      style: [styles.errorSection, errorMessage ? styles.errorSectionWithContent : undefined, stylesProp == null ? void 0 : stylesProp.error],
      id: errorId,
      testId: testId && `${testId}-error`,
      "aria-live": "assertive",
      "aria-atomic": "true"
    }, errorMessage && React.createElement(React.Fragment, null, React.createElement(PhosphorIcon, {
      icon: WarningCircle,
      style: [styles.errorIcon, styles.error],
      role: "img",
      "aria-label": labels.errorIconAriaLabel
    }), React.createElement(LabelSmall, {
      style: [styles.textWordBreak, styles.errorMessage, styles.error]
    }, errorMessage))));
  }
  function renderField() {
    return React.cloneElement(field, {
      id: fieldId,
      "aria-describedby": [errorMessage && errorId, description && descriptionId].filter(Boolean).join(" "),
      required: required || field.props.required,
      error: hasError,
      testId: testId ? `${testId}-field` : undefined
    });
  }
  return React.createElement(View, {
    style: stylesProp == null ? void 0 : stylesProp.root
  }, renderLabel(), maybeRenderDescription(), renderField(), maybeRenderError());
}
const styles = StyleSheet.create({
  label: {
    color: semanticColor.text.primary
  },
  labelWithDescription: {
    paddingBlockEnd: sizing.size_040
  },
  labelWithNoDescription: {
    paddingBlockEnd: sizing.size_120
  },
  description: {
    color: semanticColor.text.secondary,
    paddingBlockEnd: sizing.size_120
  },
  errorSection: {
    flexDirection: "row",
    gap: sizing.size_080
  },
  errorSectionWithContent: {
    paddingBlockStart: sizing.size_120
  },
  error: {
    color: semanticColor.status.critical.foreground
  },
  errorIcon: {
    marginTop: "1px"
  },
  errorMessage: {
    minWidth: "0"
  },
  required: {
    color: semanticColor.status.critical.foreground
  },
  textWordBreak: {
    overflowWrap: "break-word"
  }
});

export { LabeledField };
